import { useState, useEffect, useMemo } from 'react';
import api from '../../../hooks/useApi';
import Modal from '../../shared/Modal';
import { fmtD, fmtDY } from './planConstants';
import LoadBar, { LoadLegend } from '../../shared/LoadBar';

/*
  Who is over-committed, and because of what.

  The number that matters is not this plan's demand on somebody, it is all of
  it. A person booked four days here and three days on another SOW in the same
  week is over by two, and neither plan can see that on its own. So the grid
  shows every commitment the person has across active SOWs, with this plan's
  share called out.
*/

export default function ResourceLoadPanel({ sowId, sowReference, onClose, onOpenTask }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);   // `${person}:${week}`

  useEffect(() => {
    api.get(`sows/${sowId}/resource-load`)
      .then(setData)
      .catch(e => setError(e.message || 'Could not work out resource load.'));
  }, [sowId]);

  const overloaded = useMemo(
    () => (data?.people || []).filter(p => p.over_weeks > 0), [data]);

  return (
    <Modal title="Resource load" onClose={onClose} wide footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      {error && <div className="text-[9px] text-red-600 bg-red-50 px-2 py-1.5 rounded">{error}</div>}
      {!data && !error && <div className="py-8 text-center text-[10px] text-gray-400">Working it out…</div>}

      {data && (
        <div className="space-y-3">
          <div className="text-[8px] text-gray-500 bg-gray-50 rounded px-2 py-1.5">
            Everybody on this plan, showing <span className="font-semibold">every</span> commitment they have, against
            their own capacity: contracted hours on the days they work, less approved leave (by the half day), England
            and Wales bank holidays and company or team closures. Active SOWs are committed work; Draft and Issued SOWs,
            this one included if it is not yet active, are bid work at their win probability.
            {data.from && <> Window {fmtDY(data.from)} to {fmtDY(data.to)}.</>}
          </div>

          {/* Leave clashes come first: a task booked over somebody's holiday is
              wrong today, not a trend to watch. */}
          {(data.clashes || []).length > 0 && (
            <div className="border border-red-200 bg-red-50 rounded p-2">
              <div className="text-[9px] font-bold text-red-800 mb-1">
                {data.clashes.length} task{data.clashes.length !== 1 ? 's' : ''} scheduled over booked leave
              </div>
              <div className="space-y-0.5">
                {data.clashes.map(c => (
                  <button key={`${c.task_id}-${c.person_id}`} onClick={() => onOpenTask?.(c.task_id)}
                          className="block w-full text-left text-[8.5px] text-red-700 hover:underline">
                    <span className="font-semibold">{c.person_name}</span> is on {c.leave_type} for{' '}
                    {c.days} day{c.days !== 1 ? 's' : ''} of <span className="font-semibold">{c.task_name}</span>
                    {' '}({fmtD(c.first_day)}{c.days > 1 ? ` to ${fmtD(c.last_day)}` : ''})
                  </button>
                ))}
              </div>
            </div>
          )}

          {data.people.length === 0 ? (
            <div className="py-6 text-center text-[9px] text-gray-400">
              Nobody is assigned to a dated task on this plan yet.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-[8px]">
                <span className="text-gray-500">
                  {overloaded.length
                    ? <span className="text-red-600 font-semibold">{overloaded.length} of {data.people.length} over-committed in at least one week</span>
                    : <span className="text-emerald-600 font-semibold">Nobody is over-committed in this window</span>}
                </span>
                <span className="ml-auto"><LoadLegend /></span>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-1 text-left text-[7.5px] uppercase text-gray-400 font-bold sticky left-0 bg-gray-50 z-10">Person</th>
                      {data.weeks.map(w => (
                        <th key={w} className="px-1 py-1 text-center text-[7px] text-gray-400 font-bold whitespace-nowrap">
                          {fmtD(w)}
                        </th>
                      ))}
                      <th className="px-2 py-1 text-right text-[7.5px] uppercase text-gray-400 font-bold">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.people.map(p => (
                      <tr key={p.person_id} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-[9px] font-semibold text-gray-700 whitespace-nowrap sticky left-0 bg-white z-10">
                          {p.name}
                          {p.fte < 1 && <span className="ml-1 text-[7px] text-gray-400">{Math.round(p.fte * 100)}%</span>}
                        </td>
                        {p.weeks.map(cell => {
                          const key = `${p.person_id}:${cell.week}`;
                          const busy = cell.committed_days > 0 || cell.tentative_days > 0;
                          return (
                            <td key={cell.week} className="p-0.5 text-center align-middle min-w-[52px]">
                              <button
                                type="button"
                                onClick={() => busy && setExpanded(expanded === key ? null : key)}
                                title={[
                                  `Week commencing ${fmtDY(cell.week)}`,
                                  `Committed ${cell.committed_days}d of ${cell.capacity_days}d available`,
                                  cell.tentative_days ? `Bid ${cell.tentative_days}d` : null,
                                  cell.leave_days ? `${cell.leave_days}d leave` : null,
                                  cell.over_days ? `Over by ${cell.over_days}d` : null,
                                ].filter(Boolean).join('\n')}
                                className={`w-full rounded px-1 py-0.5 ${busy ? 'cursor-pointer hover:ring-1 hover:ring-navy' : 'cursor-default'} ${expanded === key ? 'ring-1 ring-navy' : ''}`}
                              >
                                <LoadBar committed={cell.committed_days} tentative={cell.tentative_days || 0} capacity={cell.capacity_days} compact />
                              </button>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-right text-[8px] whitespace-nowrap">
                          <span className={p.utilisation_pct > 100 ? 'text-red-600 font-bold' : 'text-gray-500'}>
                            {p.total_committed_days}d / {p.total_capacity_days}d
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* What is actually filling a week, once you click it */}
              {expanded && (() => {
                const [pid, week] = expanded.split(':');
                const p = data.people.find(x => x.person_id === pid);
                const cell = p?.weeks.find(w => w.week === week);
                if (!cell) return null;
                return (
                  <div className="border border-navy/20 bg-navy/5 rounded p-2">
                    <div className="text-[9px] font-bold text-navy mb-1">
                      {p.name}, week commencing {fmtDY(week)} — {cell.committed_days}d committed against {cell.capacity_days}d
                      {cell.over_days > 0 && <span className="text-red-600"> (over by {cell.over_days}d)</span>}
                    </div>
                    <div className="space-y-0.5">
                      {cell.tasks.map(t => (
                        <button key={t.task_id}
                                onClick={() => t.sow_id === sowId && onOpenTask?.(t.task_id)}
                                className={`block w-full text-left text-[8.5px] ${t.sow_id === sowId ? 'text-navy hover:underline' : 'text-gray-500 cursor-default'}`}>
                          <span className="font-mono text-[7.5px] text-gray-400 mr-1">
                            {t.sow_reference || 'other'}
                          </span>
                          {t.task_name}
                          {t.planned_hours ? <span className="text-gray-400"> · {t.planned_hours}h planned</span>
                            : t.units !== 1 && <span className="text-gray-400"> at {Math.round(t.units * 100)}% of their time</span>}
                          {t.tentative && <span className="text-[7px] text-indigo-700 bg-indigo-50 rounded px-1 ml-1">bid, {Math.round((t.weight ?? 0) * 100)}% likely</span>}
                          {t.sow_id !== sowId && <span className="text-[7px] text-amber-600 ml-1">another SOW</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
